import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivateFn, CanMatchFn, Route, Router, RouterStateSnapshot, UrlSegment } from '@angular/router';
import { AuthService } from '../auth/services/auth.service';
import { UsersService } from 'src/app/data/services/users.service';
import { catchError, EMPTY, filter, map, of, switchMap, take } from 'rxjs';
import { User } from '../interfaces/user';
import { StorageService } from '../services/storage.service';
import { environment } from 'src/environments/environments';
import { is } from 'date-fns/locale';

export const authGuard: CanActivateFn = (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => {
  const authService = inject(AuthService);
  const userService = inject(UsersService);
  const storageService = inject(StorageService);
  const router = inject(Router);

  const urlTree = new URL(state.url, window.location.origin);
  const isMoodle = urlTree.searchParams.get('moodle') === 'true';

  if (!authService.authenticated()) {
    if (isMoodle) {
      return router.parseUrl('/moodle-error');
    }
    return router.parseUrl('/login');
  }

  const user = userService.getUserData();
  const selectedOrg = storageService.get('selectedOrganization');

  if (user && user.organizationId === selectedOrg) {
    return of(true);
  }

  return userService.loadUser().pipe(
    map(user => {
      return user && user.organizationId === selectedOrg
        ? true
        : router.parseUrl(isMoodle ? '/moodle-error' : '/login');
    })
  );
};

// guard for login and register pages: logged users cannot access
export const loginGuard: CanActivateFn = (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => {
  const authService = inject(AuthService);
  const router = inject(Router);

  if (!authService.authenticated()) {
    return true
  }

  return router.parseUrl('/')
}

export const loginCanMatch: CanMatchFn = (route: Route, segments: UrlSegment[]) => {
  const authService = inject(AuthService);

  if (!authService.authenticated()) {
    return of(true);
  }

  return of(false);
};

// check roles
export const canMatch: CanMatchFn = (route: Route, segments: UrlSegment[]) => {
  const userService = inject(UsersService);

  return userService.user$.pipe(
    filter(user => user !== null), // Espera hasta que el usuario esté cargado
    take(1),
    map(user => {
      const allowedGroups = route.data?.['allowedGroups'] || [];
      return allowedGroups.includes(user?.group);
    })
  );
};
